// src/pages/orders/components/DeliveryFollowUpHistoryModal.jsx
import React, { useState, useEffect } from 'react';
import { X, History, User, Clock, AlertCircle } from 'lucide-react';
import { getDeliveryFollowUpNotes } from '../../../services/api/orders';
import styles from './DeliveryFollowUpHistoryModal.module.css';

const FOLLOW_UP_TYPE_LABELS = {
  customer_call: 'Customer Call',
  courier_call: 'Courier Call',
  whatsapp: 'WhatsApp',
  sms: 'SMS',
  status_update: 'Status Update',
  internal_note: 'Internal Note'
};

const RESPONSE_STATUS_LABELS = {
  pending: 'Pending',
  answered: 'Answered',
  not_answered: 'Not Answered',
  busy: 'Busy',
  switched_off: 'Switched Off',
  completed: 'Completed'
};

const RESPONSE_STATUS_STYLE_KEY = {
  pending: 'statusPending',
  answered: 'statusAnswered',
  not_answered: 'statusNotAnswered',
  busy: 'statusBusy',
  switched_off: 'statusSwitchedOff',
  completed: 'statusCompleted'
};

const formatNoteDateTime = (isoString) => {
  const date = new Date(isoString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  }).replace(',', '');
};

const DeliveryFollowUpHistoryModal = ({ orderId, onClose }) => {
  const [notes, setNotes]           = useState([]);
  const [isLoading, setIsLoading]   = useState(true);
  const [loadingError, setLoadingError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    const loadNotes = async () => {
      try {
        setIsLoading(true);
        setLoadingError(null);
        const response = await getDeliveryFollowUpNotes(orderId);
        if (!cancelled) setNotes(response.notes || []);
      } catch (err) {
        console.error('Failed to load delivery follow-up notes:', err);
        if (!cancelled) setLoadingError('Failed to load follow-up history.');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadNotes();
    return () => { cancelled = true; };
  }, [orderId]);

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3><History size={18} />Follow-up History</h3>
          <button onClick={onClose} className={styles.closeButton}>
            <X size={20} />
          </button>
        </div>

        <div className={styles.modalBody}>
          {isLoading ? (
            <div className={styles.loadingState}>
              <div className={styles.spinner} />
              <span>Loading follow-up history...</span>
            </div>
          ) : loadingError ? (
            <div className={styles.errorState}>
              <AlertCircle size={28} />
              <span>{loadingError}</span>
            </div>
          ) : notes.length === 0 ? (
            <div className={styles.emptyState}>
              <History size={32} />
              <span>No follow-up notes yet.</span>
            </div>
          ) : (
            <div className={styles.timeline}>
              {notes.map((note) => (
                <div key={note.id} className={styles.timelineItem}>
                  <div className={styles.timelineMarker}>
                    <span className={styles.timelineDot} />
                    <span className={styles.timelineLine} />
                  </div>
                  <div className={styles.timelineContent}>
                    <div className={styles.timelineTopRow}>
                      <div className={styles.timelineUser}><User size={14} />{note.createdBy?.name || 'Unknown'}</div>
                      <div className={styles.timelineDate}><Clock size={14} />{formatNoteDateTime(note.createdAt)}</div>
                    </div>
                    <div className={styles.timelineBadges}>
                      <span className={styles.typeBadge}>{FOLLOW_UP_TYPE_LABELS[note.followUpType] || note.followUpType}</span>
                      <span className={`${styles.statusBadge} ${styles[RESPONSE_STATUS_STYLE_KEY[note.responseStatus]] || ''}`}>
                        {RESPONSE_STATUS_LABELS[note.responseStatus] || note.responseStatus}
                      </span>
                    </div>
                    {note.actionTaken && (
                      <div className={styles.timelineAction}><strong>Action:</strong> {note.actionTaken}</div>
                    )}
                    {note.remarks && (
                      <div className={styles.timelineRemarks}>{note.remarks}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeliveryFollowUpHistoryModal;
